import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ChevronDown, Plus, Trash2, Save } from 'lucide-react';

// Display/suggestion-only catalog of an OTA's own room & rate-plan codes.
// Purely additive: editing/deleting here only touches the `ota_code_catalog`
// store via the handlers passed in — never any existing OTA mapping row.
const OTA_META = {
  mmt:   { label: 'MMT',   accent: 'orange', roomTitle: 'MMT Room Type Codes',   roomCodeLabel: 'OTA Room Code',       rpTitle: 'MMT Rate Plan Codes',   rpCodeLabel: 'OTA Rate Plan Code' },
  agoda: { label: 'Agoda', accent: 'red',    roomTitle: 'Agoda Room Type Codes', roomCodeLabel: 'OTA Room Code',       rpTitle: 'Agoda Rate Plan Codes', rpCodeLabel: 'OTA Rate Plan Code' },
  yatra: { label: 'Yatra', accent: 'blue',   roomTitle: 'Yatra InvCodes (Room Type Codes)', roomCodeLabel: 'InvCode', rpTitle: 'Yatra Rate Plan Codes', rpCodeLabel: 'Rate Plan Code' },
};

const CodeList = ({ ota, kind, codeLabel, items, onAdd, onUpdate, onDelete }) => {
  const [edits, setEdits] = useState({});          // { [id]: {code,name} }
  const [draft, setDraft] = useState({ code: '', name: '' });
  const [busy, setBusy] = useState(false);

  const editVal = (it, field) =>
    (edits[it.id] && edits[it.id][field] !== undefined) ? edits[it.id][field] : (it[field] || '');
  const setEdit = (id, patch) =>
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const saveRow = async (it) => {
    const code = editVal(it, 'code').trim();
    const name = editVal(it, 'name');
    if (!code) return;
    setBusy(true);
    await onUpdate(it.id, { code, name });
    setBusy(false);
  };
  const addRow = async () => {
    if (!draft.code.trim()) return;
    setBusy(true);
    await onAdd(kind, draft.code.trim(), draft.name);
    setDraft({ code: '', name: '' });
    setBusy(false);
  };

  return (
    <div className="space-y-2" data-testid={`catalog-${ota}-${kind}-list`}>
      {(items || []).length === 0 && (
        <div className="text-[11px] text-gray-400 italic">No codes yet. Add one below.</div>
      )}
      {(items || []).map(it => (
        <div key={it.id} className="flex items-center gap-2" data-testid={`catalog-${ota}-${kind}-row-${it.id}`}>
          <Input
            value={editVal(it, 'code')}
            onChange={e => setEdit(it.id, { code: e.target.value })}
            placeholder={codeLabel}
            className="text-xs h-8 flex-1"
            data-testid={`catalog-${ota}-${kind}-code-${it.id}`}
          />
          <Input
            value={editVal(it, 'name')}
            onChange={e => setEdit(it.id, { name: e.target.value })}
            placeholder="Name (optional)"
            className="text-xs h-8 flex-1"
            data-testid={`catalog-${ota}-${kind}-name-${it.id}`}
          />
          <Button
            size="sm" variant="outline" disabled={busy}
            onClick={() => saveRow(it)}
            className="h-8 px-2"
            title="Save"
            data-testid={`catalog-${ota}-${kind}-save-${it.id}`}
          ><Save className="w-3.5 h-3.5" /></Button>
          <Button
            size="sm" variant="ghost" disabled={busy}
            onClick={() => onDelete(it.id)}
            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete"
            data-testid={`catalog-${ota}-${kind}-delete-${it.id}`}
          ><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <Input
          value={draft.code}
          onChange={e => setDraft(d => ({ ...d, code: e.target.value }))}
          placeholder={`Add ${codeLabel}`}
          className="text-xs h-8 flex-1"
          data-testid={`catalog-${ota}-${kind}-new-code`}
        />
        <Input
          value={draft.name}
          onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          placeholder="Name (optional)"
          className="text-xs h-8 flex-1"
          data-testid={`catalog-${ota}-${kind}-new-name`}
        />
        <Button
          size="sm" variant="outline" disabled={busy || !draft.code.trim()}
          onClick={addRow}
          className="h-8 px-3 whitespace-nowrap"
          data-testid={`catalog-${ota}-${kind}-add`}
        ><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
      </div>
    </div>
  );
};

export const OtaCodeCatalogSection = ({ ota, propertyId, catalog, onAdd, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const meta = OTA_META[ota] || OTA_META.mmt;
  const rooms = (catalog && catalog.room) || [];
  const ratePlans = (catalog && catalog.rate_plan) || [];

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-4 border rounded-lg bg-white">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          data-testid={`catalog-toggle-${ota}-${propertyId}`}
        >
          <span>{meta.label} Room &amp; Rate Codes</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-5" data-testid={`catalog-content-${ota}-${propertyId}`}>
        <div className="text-[11px] text-gray-500 -mt-1">
          Reference codes used for OTA Mapping suggestions. Editing here does not change any saved mapping.
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-2">{meta.roomTitle}</div>
          <CodeList
            ota={ota} kind="room" codeLabel={meta.roomCodeLabel} items={rooms}
            onAdd={onAdd} onUpdate={onUpdate} onDelete={onDelete}
          />
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-2">{meta.rpTitle}</div>
          <CodeList
            ota={ota} kind="rate_plan" codeLabel={meta.rpCodeLabel} items={ratePlans}
            onAdd={onAdd} onUpdate={onUpdate} onDelete={onDelete}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default OtaCodeCatalogSection;
